/*
 * WarehouseFilterSelect — Feb 2026
 * --------------------------------
 * Shared top-of-page warehouse selector for AdminStock, FloorTasks,
 * Warehouse Tasks and any other view that lists per-warehouse
 * inventory or work. Persists the selection in localStorage keyed by
 * `mr_wh_filter:${storageKey}` so a user's choice is remembered
 * across tabs and page reloads within the same page.
 *
 * Semantics:
 *   * `value = ""`  → "All my warehouses" (backend already scopes to
 *                     the caller's `warehouse_scope`)
 *   * `value = "<id>"` → only rows with `warehouse_id === value`
 *
 * The component only renders when the caller has >1 accessible
 * warehouse — a single-site user doesn't need a filter and the
 * screen stays clean.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Warehouse as WarehouseIcon } from 'lucide-react';
import { API } from '../context/AdminAuthContext';

export const useWarehouseFilter = (storageKey) => {
  const [warehouses, setWarehouses] = useState([]);
  const [value, setValueState] = useState(() => {
    try {
      return localStorage.getItem(`mr_wh_filter:${storageKey}`) || '';
    } catch (_e) {
      return '';
    }
  });

  const setValue = (v) => {
    const next = v || '';
    setValueState(next);
    try {
      if (next) localStorage.setItem(`mr_wh_filter:${storageKey}`, next);
      else localStorage.removeItem(`mr_wh_filter:${storageKey}`);
    } catch (_e) { /* ignore */ }
  };

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const token = localStorage.getItem('mr_admin_token') || '';
        const { data } = await axios.get(`${API}/admin/warehouses`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!live) return;
        const list = Array.isArray(data?.warehouses) ? data.warehouses : [];
        setWarehouses(list);
        // If the persisted value points at a warehouse the user can
        // no longer access (permissions changed), silently reset.
        if (value && !list.some((w) => w.id === value)) {
          setValue('');
        }
      } catch (_e) { /* ignore — filter just won't render */ }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { warehouses, value, setValue };
};

export const WarehouseFilterSelect = ({
  storageKey,
  warehouses,
  value,
  onChange,
  className = '',
}) => {
  if (!Array.isArray(warehouses) || warehouses.length <= 1) return null;
  return (
    <label
      className={`inline-flex items-center gap-2 ${className}`}
      data-testid={`wh-filter-wrap-${storageKey}`}
    >
      <WarehouseIcon className="w-4 h-4 text-neutral-400" />
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 px-2 rounded-md border border-neutral-300 bg-white text-xs font-medium text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40 focus:border-[#0078D4]"
        data-testid={`wh-filter-${storageKey}`}
        aria-label="Filter by warehouse"
      >
        <option value="">All my warehouses</option>
        {warehouses.map((w) => (
          <option
            key={w.id}
            value={w.id}
            data-testid={`wh-filter-${storageKey}-option-${w.id}`}
          >
            {w.name}
            {w.is_default ? ' · default' : ''}
          </option>
        ))}
      </select>
    </label>
  );
};

export default WarehouseFilterSelect;
